/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import Iframe from 'react-iframe';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { blue } from '@mui/material/colors';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDSnackbar from 'components/MDSnackbar';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import CheckTag from './CheckTag';
import ProductUploadTag from './ProductUploadTag';
import ProductImage from './ProductImage';
import ProductBranch from './ProductBranch';
import ProductSupplier from './ProductSupplier';

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  print_tag: false,
};

const initPrintUrl = {
  print_product: '/api/product_print-print',
  print_producttag: '/api/product_printtag-print',
  print_campaign: '/api/campaign_print-print',
  print_campaigntag: '/api/campaign_printtag-print',
};

function ProductSKU() {
  const mod = 'product_sku';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [printType, setPrintType] = useState('');
  const [printUrl, setPrintUrl] = useState(initPrintUrl);
  const [imageTag, setImageTag] = useState({});
  const [photo, setPhoto] = useState({});
  const [modalPhoto, setModalPhoto] = useState(false);
  const [modalBranch, setModalBranch] = useState(false);
  /* ข้อ 6 (มติประชุมลูกค้า 5 ต.ค. 2569) — ป้ายแคมเปญพิมพ์เฉพาะแคมเปญที่ใช้อยู่
     ผู้มีสิทธิ์ campaign_expired ติ๊กรวมแคมเปญนอกช่วงวันที่ได้ · ค่าตั้งต้นไม่ติ๊กเสมอ */
  const [includeExpired, setIncludeExpired] = useState(false);
  const canPrintExpired = Boolean(app && app.perm && app.perm.campaign_expired);
  const [buttonGroup, setButtonGroup] = useState(null);
  const [cboBranch, setCboBranch] = useState([]);
  const [modalPrint, setModalPrint] = useState(false);
  const [urlPrint, setUrlPrint] = useState('');

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.id', label: t(`attr.image`), align: 'center', width: 40 },
    { id: 'pd.barcode', label: 'Barcode', align: 'left', width: 100 },
    { id: 'pd.name', label: 'สินค้า', align: 'left' },
    { id: 'tb.bom_id', label: 'สูตรผลิต', align: 'left' },
    { id: 'tb.unit_id', label: 'หน่วย', align: 'left', width: 120 },
    { id: 'pd.cost', label: 'ซื้อ', align: 'right', width: 120 },
    { id: 'pd.price', label: 'ราคา', align: 'right', width: 120 },
    { id: 'pd.product_main', label: 'Main', align: 'center', width: 40 },
    { id: 'tb.enable', label: 'POS', align: 'center', width: 40, sort: false },
    { id: 'scan', label: 'Scan', align: 'center', width: 40, sort: false },
    { id: 'qrcode', label: 'Info', align: 'center', width: 40, sort: false },
    { id: 'preview', label: 'View', align: 'center', width: 40, sort: false },
    // { id: "tag", label: "พิมพ์", align: "center", width: 40, sort: false },
  ];

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      print_tag: data.print_tag ? 'Y' : 'N',
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const reload = () => {
    view(find);
  };

  const print = (item) => {
    setModalBranch(false);
    const expiredQuery = canPrintExpired && includeExpired ? '?expired=Y' : '';
    setIncludeExpired(false);

    if (printType === 'product') {
      setUrlPrint(`${printUrl.print_product}/${item.code}`);
      setModalPrint(true);
    } else if (printType === 'product_tag') {
      setUrlPrint(`${printUrl.print_producttag}/${item.code}`);
      setModalPrint(true);
    } else if (printType === 'campaign') {
      setUrlPrint(`${printUrl.print_campaign}/${item.code}${expiredQuery}`);
      setModalPrint(true);
    } else if (printType === 'campaign_tag') {
      setUrlPrint(`${printUrl.print_campaigntag}/${item.code}${expiredQuery}`);
      setModalPrint(true);
    }
  };

  const clear = () => {
    setButtonGroup(null);
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setRecord([]);
        setLoading(true);

        FetchData('PUT', `/${mod}`, app.token, {}).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            view({ ...find, print_tag: false, page: 1 });
            showNotify(t(`alert.savecomplete`), 'success');
          } else {
            showNotify(t(`alert.saveerror`), 'error');
          }
        });
      }
    });
  };

  const upload = (filedata) => {
    setButtonGroup(null);
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/upload`, app.token, {
      filedata: filedata,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        setImageTag(result.image);
        setPhoto(result.image);
        setModalPhoto(true);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const edit = (item) => {
    FetchData('PATCH', `/${mod}/${item.id}`, app.token, {
      print_tag: item.print_tag,
    }).then((result) => {
      if (result.status !== 'ok') {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setImageTag(result.image_tag);
        setPhoto(result.image_tag);
        setPrintUrl(result.print);
        setCboBranch(result.branch);
        view(find);
      } else {
        Func.exitApp();
      }
    });
  };

  const pin = (item) => {
    setLoading(true);

    FetchData('OPTIONS', `/product_barcode/pin`, app.token, {
      id: item.id,
      image_id: item.image_id,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const qrCode = (item) => {
    FetchData('PATCH', `/rp_product_barcode/${item.id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setPhoto({
          image: result.url,
          name: item.barcode,
        });
        setModalPhoto(true);
      }
      setLoading(false);
    });
  };

  const btnQrCode = (item) => {
    if (item.qrcode === '') return null;

    return (
      <MDButton
        variant='text'
        color='error'
        size='small'
        iconOnly
        onClick={() => {
          setPhoto({
            image: item.image_qr,
            name: item.product_name,
          });
          setModalPhoto(true);
        }}
      >
        <Icon>qr_code_scanner_outlined</Icon>
      </MDButton>
    );
  };

  const btnQrProduct = (item) => {
    return (
      <MDButton variant='text' color='success' size='small' iconOnly onClick={() => qrCode(item)}>
        <Icon>qr_code_scanner_outlined</Icon>
      </MDButton>
    );
  };

  const btnPreview = (item) => {
    return (
      <MDButton
        variant='text'
        color='info'
        size='small'
        iconOnly
        onClick={() => {
          window.open(item.url_info, '_blank');
        }}
      >
        <Icon>search</Icon>
      </MDButton>
    );
  };

  const renderButton = () => {
    return (
      <MDBox display='flex' justifyContent='end' gap={1}>
        {find.print_tag ? (
          <MDButton
            variant='outlined'
            color='success'
            onClick={() => {
              view({ ...find, print_tag: !find.print_tag, page: 1 });
            }}
          >
            <Icon>rule</Icon>&nbsp;แสดงที่เลือก
          </MDButton>
        ) : (
          <MDButton
            color='success'
            onClick={() => {
              view({ ...find, print_tag: !find.print_tag, page: 1 });
            }}
          >
            <Icon>task_alt_outlined</Icon>&nbsp;แสดงทั้งหมด
          </MDButton>
        )}
        <MDButton color='primary' onClick={clear}>
          <Icon>highlight_off_outlined</Icon>&nbsp;ล้างรายการพิมพ์
        </MDButton>
        <MDButton variant='gradient' color='info' onClick={(e) => setButtonGroup(e.currentTarget)}>
          <Icon>print</Icon>&nbsp;พิมพ์
        </MDButton>
        <Menu
          sx={{ mt: '45px' }}
          id='menu-appbar'
          anchorEl={buttonGroup}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(buttonGroup)}
          onClose={() => setButtonGroup(null)}
        >
          <MenuItem
            onClick={() => {
              setButtonGroup(null);
              setModalBranch(true);
              setPrintType('product');
            }}
          >
            <MDTypography variant='body2'>
              <Icon>local_printshop</Icon>&nbsp;พิมพ์ TAG สินค้า A4
            </MDTypography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setButtonGroup(null);
              setModalBranch(true);
              setPrintType('product_tag');
            }}
          >
            <MDTypography variant='body2'>
              <Icon>local_printshop</Icon>&nbsp;พิมพ์ TAG สินค้า Sticker
            </MDTypography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setButtonGroup(null);
              setModalBranch(true);
              setPrintType('campaign');
            }}
          >
            <MDTypography variant='body2'>
              <Icon>local_printshop</Icon>&nbsp;พิมพ์ TAG Campaign A4
            </MDTypography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setButtonGroup(null);
              setModalBranch(true);
              setPrintType('campaign_tag');
            }}
          >
            <MDTypography variant='body2'>
              <Icon>local_printshop</Icon>&nbsp;พิมพ์ TAG Campaign Sticker
            </MDTypography>
          </MenuItem>
        </Menu>
      </MDBox>
    );
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12} lg={4}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={8}>
            &nbsp;
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center'>
                        <ProductImage parent={row} callback={pin} />
                      </TableCell>
                      <TableCell>
                        <MDBox>{row.barcode}</MDBox>
                        <MDBox>{row.barcode2 === row.barcode ? '' : row.barcode2}</MDBox>
                      </TableCell>
                      <TableCell>
                        <MDBox>{row.code}</MDBox>
                        <MDBox className='nowrap'>{row.name}</MDBox>
                      </TableCell>
                      <TableCell align='left'>{row.bom_name}</TableCell>
                      <TableCell align='left'>{row.unit_name}</TableCell>
                      <TableCell align='right'>
                        <ProductSupplier parent={row} reload={reload} />
                      </TableCell>
                      <TableCell align='right'>
                        <ProductBranch parent={row} reload={reload} />
                      </TableCell>
                      <TableCell align='center'>{Func.enableDisplay(row.product_main)}</TableCell>
                      <TableCell align='center'>{Func.enableDisplay(row.enable)}</TableCell>
                      <TableCell align='center'>{btnQrProduct(row)}</TableCell>
                      <TableCell align='center'>{btnQrCode(row)}</TableCell>
                      <TableCell align='center'>{btnPreview(row)}</TableCell>
                      {/* <TableCell align="center">
                        <CheckTag parent={row} callback={edit} />
                      </TableCell> */}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderBranch = () => {
    return (
      <Dialog
        onClose={() => {
          setModalBranch(false);
          setIncludeExpired(false);
        }}
        open={modalBranch}
      >
        <DialogTitle>เลือกสาขา</DialogTitle>
        <List sx={{ pt: 0 }}>
          {cboBranch.map((item) => (
            <ListItem disablePadding key={item.id}>
              <ListItemButton onClick={() => print(item)}>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: blue[100], color: blue[600] }}>
                    <Icon>store</Icon>
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={item.name} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        {canPrintExpired && (printType === 'campaign' || printType === 'campaign_tag') ? (
          <MDBox px={2} pb={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeExpired}
                  onChange={(e) => setIncludeExpired(e.target.checked)}
                />
              }
              label='รวมแคมเปญที่หมดอายุ/ยังไม่เริ่ม (สิทธิ์พิเศษ)'
            />
          </MDBox>
        ) : null}
      </Dialog>
    );
  };

  const renderPhoto = () => {
    return (
      <Dialog
        open={modalPhoto}
        maxWidth='sm'
        keepMounted
        aria-describedby='dialog-image'
        fullWidth
        onClose={() => setModalPhoto(false)}
      >
        <DialogTitle>
          {photo.name}
          <IconButton
            aria-label='close'
            onClick={() => setModalPhoto(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox
            component='img'
            src={photo.image}
            alt={photo.name}
            borderRadius='lg'
            shadow='sm'
            width='100%'
            position='relative'
          />
        </DialogContent>
      </Dialog>
    );
  };

  const renderPrint = () => {
    return (
      <Dialog
        open={modalPrint}
        maxWidth='xxl'
        keepMounted
        aria-describedby='dialog-print'
        fullScreen
        onClose={() => {
          setModalPrint(false);
          setUrlPrint('');
        }}
      >
        <DialogTitle>
          Print
          <IconButton
            aria-label='close'
            onClick={() => {
              setModalPrint(false);
              setUrlPrint('');
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {urlPrint === '' ? null : (
            <Iframe
              url={urlPrint}
              width='100%'
              height='100%'
              id='print'
              className=''
              display='initial'
              position='relative'
              frameBorder='0'
            />
          )}
        </DialogContent>
      </Dialog>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.product`), t(`menu.product_sku`)]} />
      {renderList()}
      {renderPhoto()}
      {renderBranch()}
      {renderPrint()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default ProductSKU;
